// Scrollable transcript. Two layout tricks make the "pin user message to
// top on send" UX work:
//
//   1. `containerType: 'size'` on the scroll viewport turns it into a CSS
//      query container, so descendants can reference its height via the
//      `cqh` unit instead of falling back to the document viewport.
//
//   2. `paddingBottom: 100cqh` while we're awaiting a reply reserves one
//      viewport of empty space below the last message — without it the
//      scroll container has nothing to scroll into past the bottom edge
//      and the new user bubble sits flush instead of pinning to the top.
//      Standard ChatGPT / Claude pattern. The padding collapses to `auto`
//      once the assistant has responded so settled conversations don't
//      leave a viewport-tall void below the last bubble.
//
// The scroll container fills the full ThreadContainer width; the inner
// column applies the configured max-width so messages stay centered while
// the scrollbar still hugs the right edge of the panel.

import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { Message } from '../Message';
import { SpinnerMessage } from '../SpinnerMessage';
import { ThreadBodySkeleton } from '../ThreadBodySkeleton';
import { isInProgress, isUserMessage } from '../../utils/messageUtils';
import type { ThreadBodyProps } from './ThreadBody.types';

export const ThreadBody = ({
  messages,
  scrollRef,
  bodyContainerProps,
  isAwaitingReply,
  isInitialLoading,
  streamingSubSteps,
}: ThreadBodyProps) => {
  // Show the "Thinking…" card while we're waiting and the last message is
  // the user's. Once text starts streaming the draft becomes the last
  // message, so this turns off automatically.
  const lastMessage = messages[messages.length - 1];
  const showThinking =
    isAwaitingReply && !!lastMessage && isUserMessage(lastMessage);

  // Once the draft renders (cards/text on screen) the Thinking card turns
  // off — but the turn may still be running (final response composing,
  // more tools queued). Without a signal that window reads as "everything
  // is frozen": inputs are disabled and nothing moves. Show a compact
  // working strip naming the current step (e.g. "Composing response…"),
  // falling back to a generic label between steps.
  const showWorkingStrip = isAwaitingReply && !showThinking;
  const activeStepLabel = [...(streamingSubSteps ?? [])]
    .reverse()
    .find((s) => s.status === 'active')?.name;

  return (
    <Box
      ref={scrollRef}
      role="log"
      aria-label="Conversation"
      aria-live="polite"
      flex="1"
      minH={0}
      overflowY="auto"
      px="16px"
      py="16px"
      sx={{ containerType: 'size' }}
    >
      <Box mx="auto" {...bodyContainerProps}>
        {isInitialLoading ? (
          <ThreadBodySkeleton />
        ) : (
          <Flex
            direction="column"
            gap="20px"
            sx={{ paddingBottom: isAwaitingReply ? '100cqh' : '16px' }}
          >
            {messages.map((message) => (
              <Box key={message.id} data-message-id={message.id}>
                {isInProgress(message) ? (
                  <SpinnerMessage detail={message.content || undefined} />
                ) : (
                  <Message message={message} />
                )}
              </Box>
            ))}
            {showThinking ? (
              <Box data-test-id="thread-thinking">
                <SpinnerMessage
                  startedAt={lastMessage.created_at}
                  subSteps={streamingSubSteps}
                />
              </Box>
            ) : null}
            {showWorkingStrip ? (
              <Flex
                align="center"
                gap="8px"
                data-test-id="thread-working-strip"
              >
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="primary-500"
                  className="animate-pulse"
                  flexShrink={0}
                />
                <Text variant="small" className="text-secondary-500">
                  {activeStepLabel ? `${activeStepLabel}…` : 'Still working…'}
                </Text>
              </Flex>
            ) : null}
          </Flex>
        )}
      </Box>
    </Box>
  );
};
